import { ActionStatusHelper } from "@/Shared/Helpers";
import { useEffect, useState } from "react";
import { useFormSubmit } from "../useFormSubmit";
import { DOMAIN_CONFIGS, DomainHookProps } from "@/Shared";
import { ActionResponse, DomainType, Entity, EntityHook } from "@/Shared/Types";

/**
 * =====================================================
 * Hook: useFormLifeCycle
 * =====================================================
 *
 * Description:
 * Generic lifecycle orchestration hook for domain forms.
 *
 * This hook centralizes and standardizes the full form workflow
 * across all domains, including:
 * - Entity initialization (create / edit mode)
 * - Data fetching and resolution
 * - ViewModel construction
 * - Submission handling (create / update)
 * - Post-submit extension logic
 * - Global response computation
 *
 * It acts as the foundation layer for all domain-specific
 * form hooks (e.g., useActivityForm, useNeedForm, etc.).
 *
 * Responsibilities:
 * - Resolve current entity (queryId / entityId logic)
 * - Guard initialization based on combined response status
 * - Build and expose the FormViewModel
 * - Delegate submission logic to useFormSubmit
 * - Handle redirect behavior (modal vs page mode)
 * - Expose a unified and consistent public API
 *
 * Behavior:
 * - Waits until all required dependencies are successfully loaded
 * - Fetches the current entity when in edit mode
 * - Builds the ViewModel using injected buildVM function
 * - Delegates submission to create or update automatically
 * - Optionally triggers domain-specific post-success logic
 * - Protects against state updates after unmount
 *
 * Author: Samano CASTRE
 * Date: March 2, 2026
 */
export const useFormLifeCycle = <FormViewModel, Model extends Entity>(
    config: {
        response: ActionResponse,
        domainType: DomainType,
        props: DomainHookProps,
        entityHook: EntityHook<Model>,
        redirectOnSuccess?: boolean,
        buildVM: (currentEntity: Model) => FormViewModel,
        onSuccess? : (entity : Model) => Promise<void>
    }
) => {

    const {
        response,
        domainType,
        props,
        entityHook,
        redirectOnSuccess,
        buildVM,
        onSuccess
    } = config;

    const { extraArgs, inModalHook, accessRight, uiFeedbackHooks } = props;
    const { entityId } = extraArgs ?? {};
    const inModal = !!inModalHook;
    const { domainLabel } = DOMAIN_CONFIGS[domainType];

    /**
     * Entity Hook Operations
     *
     * Provides:
     * - queryId resolution
     * - dataMap access
     * - current entity fetching
     * - CRUD actions
     */
    const {
        queryId,
        dataMap,
        findCurrentAction,
        updateAction,
        createAction,
    } = entityHook;
   

    /**
     * ViewModel State
     * 
     * Holds the constructed FormViewModel
     * exposed to the UI layer.
     */
    const [entityFormVM, setEntityFormVM] = useState<FormViewModel>();

     /**
     * Submission Logic
     *
     * Delegates form submission workflow:
     * - create vs update resolution
     * - permission enforcement
     * - UI feedback handling
     * - optional redirect
     * - optional post-success domain logic
     */
    const { handleSubmit, formResponse } = useFormSubmit({
        domainType,
        domainLabel,
        accessRight,
        uiFeedbackHooks,
        createAction,
        updateAction,
        entityId: inModal ? entityId : queryId,
        redirectOnSuccess: redirectOnSuccess === undefined ? !inModal : redirectOnSuccess,
        inModalHook,
        onSuccess
    });

    /**
     * Request Identifier
     *
     * Unified identifier used by the UI
     * to determine create or edit mode.
     */
    const requestId = entityId ?? queryId;

     /**
     * ViewModel Initialization Effect
     *
     * Triggers when:
     * - All combined responses are successful
     * - Entity identifiers change
     *
     * Behavior:
     * - Resolves current entity (edit mode only)
     * - Builds the FormViewModel
     * - Prevents state update after unmount
     */
    useEffect(() => {

        if (!ActionStatusHelper.isSucceeded(response.status)) {
            return;
        }
        let cancelled = false;

        const init = async () => {
            if (cancelled) return;
            const initCurrentEntity = async () => {
                if (entityId) {
                    return dataMap.get(entityId);
                }
                if (!inModal && queryId) {
                    return await findCurrentAction.execute();
                }
                return undefined;
            }

            const currentEntity = await initCurrentEntity();
            
            if (
                cancelled
                || (entityId && !currentEntity)
                || (queryId && !inModal && !currentEntity)
            ) {
                return;
            }

            setEntityFormVM(buildVM(currentEntity));
            
        }

        init();
        return () => {
            cancelled = true;
        };

    }, [
        response.status,
        queryId,
        entityId,
        domainType,
        inModal
    ]);

    /**
     * Response Computation
     *
     * Ensures correct error propagation when:
     * - findCurrentAction fails outside modal context
     */
    const computeResponse = () => {
        if (inModal || !findCurrentAction.response.error) {
            return response;
        }

        return findCurrentAction.response;
    }

    /**
     * Public API
     *
     * Exposes only the data and actions required
     * by the UI layer.
     */
    return {
        // Form
        entityFormVM,
        handleSubmit,
        formResponse,
        response: computeResponse(),
        accessRight,
        uiFeedbackHooks,
        requestId
    }
}